import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { ArrowRight, Ban, Check, CheckCheck, Plus, Send, Wand2 } from "lucide-react";
import { api, errMsg, inr } from "../lib/api";
import { useCompany } from "../context/CompanyContext";
import { EmptyState, Money, PageHeader, StatusBadge, TableSkeleton, fmtDate } from "../components/erp";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from "../components/ui/dialog";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs";
import { Checkbox } from "../components/ui/checkbox";
import { Textarea } from "../components/ui/textarea";

const MODES = ["cash", "bank_transfer", "upi", "cheque", "neft", "rtgs", "imps", "other"];
const PO_LABEL = { draft: "Draft", pending_approval: "Pending Approval", approved: "Approved", completed: "Completed", converted: "Converted", cancelled: "Cancelled" };

export function PayOutDialog({ open, onClose, presetParty, onSaved }) {
  const [parties, setParties] = useState([]);
  const [partyId, setPartyId] = useState(presetParty || "");
  const [date, setDate] = useState(new Date().toISOString().slice(0, 10));
  const [amount, setAmount] = useState("");
  const [mode, setMode] = useState("bank_transfer");
  const [reference, setReference] = useState("");
  const [notes, setNotes] = useState("");
  const [openDocs, setOpenDocs] = useState([]);
  const [allocs, setAllocs] = useState({});
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!open) return;
    setPartyId(presetParty || ""); setAmount(""); setReference(""); setNotes(""); setAllocs({});
    api.get("/parties?limit=200").then((r) => setParties(r.data.items.filter((p) => !p.archived))).catch(() => {});
  }, [open, presetParty]);

  useEffect(() => {
    if (!partyId) { setOpenDocs([]); return; }
    api.get("/payables").then((r) => setOpenDocs((r.data.items || []).filter((i) => i.party_id === partyId))).catch(() => setOpenDocs([]));
  }, [partyId]);

  const allocated = useMemo(() => Object.values(allocs).reduce((s, a) => s + (+a.amount || 0), 0), [allocs]);
  const diff = (+amount || 0) - allocated;

  const doAuto = () => {
    let remaining = +amount || 0;
    const next = {};
    for (const d of openDocs) {
      if (remaining <= 0) break;
      const amt = Math.min(remaining, d.balance);
      next[d.id] = { invoice_id: d.id, amount: amt };
      remaining -= amt;
    }
    setAllocs(next);
    toast.info("Allocated to oldest bills first");
  };

  const save = async () => {
    if (!partyId || !(+amount > 0)) { toast.error("Select supplier and enter amount"); return; }
    setBusy(true);
    try {
      const { data } = await api.post("/payments-out", {
        party_id: partyId, date, amount: +amount, mode, reference, notes,
        allocations: Object.values(allocs).filter((a) => +a.amount > 0).map((a) => ({ invoice_id: a.invoice_id, amount: +a.amount })),
      });
      toast.success(`Payment ${data.payment_no} recorded` + (data.unallocated > 0 ? ` · ${inr(data.unallocated)} supplier prepaid` : ""));
      onSaved(); onClose();
    } catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); }
  };

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent aria-describedby={undefined} className="max-w-2xl max-h-[90vh] overflow-y-auto bg-white dark:bg-slate-900" data-testid="payout-dialog">
        <DialogHeader><DialogTitle className="font-display">Record Payment Out</DialogTitle></DialogHeader>
        <div className="grid grid-cols-2 gap-4">
          <div className="col-span-2"><Label>Supplier *</Label>
            <Select value={partyId} onValueChange={setPartyId}>
              <SelectTrigger data-testid="payout-party" className="mt-1.5"><SelectValue placeholder="Select supplier" /></SelectTrigger>
              <SelectContent>{parties.map((p) => <SelectItem key={p.id} value={p.id}>{p.name} ({p.code})</SelectItem>)}</SelectContent>
            </Select>
          </div>
          <div><Label>Amount (₹) *</Label><Input data-testid="payout-amount" type="number" min="0" className="mt-1.5" value={amount} onChange={(e) => setAmount(e.target.value)} /></div>
          <div><Label>Date</Label><Input type="date" className="mt-1.5" value={date} onChange={(e) => setDate(e.target.value)} /></div>
          <div><Label>Mode</Label>
            <Select value={mode} onValueChange={setMode}>
              <SelectTrigger data-testid="payout-mode" className="mt-1.5"><SelectValue /></SelectTrigger>
              <SelectContent>{MODES.map((m) => <SelectItem key={m} value={m} className="capitalize">{m.replace(/_/g, " ")}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          <div><Label>Reference No</Label><Input className="mt-1.5" value={reference} onChange={(e) => setReference(e.target.value)} /></div>
          <div className="col-span-2"><Label>Notes</Label><Textarea rows={2} className="mt-1.5" value={notes} onChange={(e) => setNotes(e.target.value)} /></div>
        </div>
        {partyId && (
          <div className="mt-2 rounded-lg border border-slate-200 dark:border-slate-800">
            <div className="flex items-center justify-between bg-slate-50 dark:bg-slate-800/60 px-3 py-2">
              <p className="text-xs font-semibold uppercase tracking-wider text-slate-500">Allocate to open bills</p>
              <Button size="sm" variant="ghost" data-testid="payout-auto-allocate" onClick={doAuto} disabled={!amount || openDocs.length === 0}>
                <Wand2 className="mr-1 h-3.5 w-3.5" /> Auto Allocate
              </Button>
            </div>
            {openDocs.length === 0 ? (
              <p className="p-4 text-sm text-slate-400">No open bills — the full amount becomes supplier prepaid.</p>
            ) : (
              <div className="max-h-56 overflow-y-auto">
                {openDocs.map((d) => {
                  const a = allocs[d.id];
                  return (
                    <div key={d.id} className="flex items-center gap-3 border-b border-slate-100 dark:border-slate-800 px-3 py-2 text-sm">
                      <Checkbox checked={!!a} data-testid={`payout-alloc-${d.doc_no}`}
                        onCheckedChange={(c) => setAllocs((p) => { const n = { ...p }; if (c) n[d.id] = { invoice_id: d.id, amount: d.balance }; else delete n[d.id]; return n; })} />
                      <div className="flex-1 min-w-0">
                        <p className="font-mono text-xs font-semibold truncate">{d.doc_no}</p>
                        <p className="text-xs text-slate-500">{fmtDate(d.date)}</p>
                      </div>
                      <span className="text-xs text-slate-500">bal <Money value={d.balance} /></span>
                      {a && <Input type="number" min="0" max={d.balance} className="h-8 w-28 font-mono text-xs" value={a.amount}
                        onChange={(e) => setAllocs((p) => ({ ...p, [d.id]: { invoice_id: d.id, amount: Math.min(+e.target.value || 0, d.balance) } }))} />}
                    </div>
                  );
                })}
              </div>
            )}
            <div className="grid grid-cols-3 gap-2 px-3 py-2.5 text-xs font-medium bg-slate-50 dark:bg-slate-800/60">
              <span>Payment: <b className="font-mono">{inr(+amount || 0)}</b></span>
              <span>Allocated: <b className="font-mono text-emerald-600">{inr(allocated)}</b></span>
              <span className={diff < 0 ? "text-red-600" : diff > 0 ? "text-amber-600" : "text-slate-500"}>
                {diff < 0 ? "Over-allocated" : diff > 0 ? `Prepaid: ${inr(diff)}` : "Fully allocated"}
              </span>
            </div>
          </div>
        )}
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button data-testid="payout-save-btn" onClick={save} disabled={busy || diff < 0}
            className="bg-slate-900 dark:bg-slate-100 dark:text-slate-900">{busy ? "Saving..." : "Record Payment"}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export default function Purchases() {
  const { activeId } = useCompany();
  const [tab, setTab] = useState("bills");
  const [bills, setBills] = useState(null);
  const [orders, setOrders] = useState(null);
  const [payables, setPayables] = useState(null);
  const [payouts, setPayouts] = useState(null);
  const [payDialog, setPayDialog] = useState(false);
  const nav = useNavigate();

  const load = () => {
    api.get("/purchase-invoices?limit=200").then((r) => setBills(r.data.items)).catch(() => setBills([]));
    api.get("/purchase-orders?limit=200").then((r) => setOrders(r.data.items)).catch(() => setOrders([]));
    api.get("/payables").then((r) => setPayables(r.data)).catch(() => setPayables({ items: [] }));
    api.get("/payments-out?limit=200").then((r) => setPayouts(r.data.items)).catch(() => setPayouts([]));
  };
  useEffect(() => { if (activeId) { setBills(null); setOrders(null); setPayables(null); setPayouts(null); load(); } }, [activeId]); // eslint-disable-line

  const poAction = async (po, status) => {
    try {
      await api.post(`/purchase-orders/${po.id}/status`, { status });
      toast.success(`PO ${po.doc_no} → ${PO_LABEL[status]}`);
      load();
    } catch (e) { toast.error(errMsg(e)); }
  };
  const poConvert = async (po) => {
    try {
      const { data } = await api.post(`/purchase-orders/${po.id}/convert`);
      toast.success(`Draft purchase bill ${data.doc_no} created — review and post it`);
      setTab("bills");
      load();
    } catch (e) { toast.error(errMsg(e)); }
  };
  const postBill = async (b) => {
    try {
      await api.post(`/purchase-invoices/${b.id}/post`);
      toast.success(`Bill ${b.doc_no} posted — stock updated`);
      load();
    } catch (e) { toast.error(errMsg(e)); }
  };
  const cancelBill = async (b) => {
    try {
      await api.post(`/purchase-invoices/${b.id}/cancel`);
      toast.success(`Bill ${b.doc_no} cancelled and reversed`);
      load();
    } catch (e) { toast.error(errMsg(e)); }
  };
  const cancelPayout = async (p) => {
    try {
      await api.post(`/payments-out/${p.id}/cancel`);
      toast.success(`Payment ${p.payment_no} cancelled`);
      load();
    } catch (e) { toast.error(errMsg(e)); }
  };

  return (
    <div data-testid="purchases-page">
      <PageHeader title="Purchases" testId="purchases-title" sub="Purchase orders, supplier bills, payables and payments out">
        <Button variant="outline" data-testid="new-po-btn" onClick={() => nav("/purchases/orders/new")}>
          <Plus className="mr-1.5 h-4 w-4" /> Purchase Order
        </Button>
        <Button data-testid="new-bill-btn" onClick={() => nav("/purchases/invoices/new")}
          className="bg-slate-900 dark:bg-slate-100 dark:text-slate-900"><Plus className="mr-1.5 h-4 w-4" /> Purchase Bill</Button>
      </PageHeader>

      <Tabs value={tab} onValueChange={setTab}>
        <TabsList className="mb-4 flex-wrap h-auto">
          <TabsTrigger value="bills" data-testid="tab-purchase-bills">Purchase Bills</TabsTrigger>
          <TabsTrigger value="orders" data-testid="tab-purchase-orders">Purchase Orders</TabsTrigger>
          <TabsTrigger value="payables" data-testid="tab-payables">Payables</TabsTrigger>
          <TabsTrigger value="payouts" data-testid="tab-payouts">Payments Out</TabsTrigger>
        </TabsList>

        <TabsContent value="bills">
          <div className="rounded-xl border border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-900 overflow-hidden">
            {!bills ? <TableSkeleton /> : bills.length === 0 ? (
              <EmptyState testId="bills-empty" title="No purchase bills" body="Post a supplier bill to increase stock and track payables." actionLabel="New Bill" actionTo="/purchases/invoices/new" />
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full text-sm" data-testid="bills-table">
                  <thead><tr className="bg-slate-50 dark:bg-slate-800/60 text-left text-xs font-semibold uppercase tracking-wider text-slate-500">
                    <th className="p-3">Bill No</th><th className="p-3">Date</th><th className="p-3">Supplier</th>
                    <th className="p-3">Supplier Inv</th><th className="p-3 text-right">Total</th><th className="p-3 text-right">Paid</th>
                    <th className="p-3 text-right">Balance</th><th className="p-3">Status</th><th className="p-3"></th></tr></thead>
                  <tbody>
                    {bills.map((b) => (
                      <tr key={b.id} data-testid={`bill-row-${b.doc_no}`} className="border-b border-slate-100 dark:border-slate-800 hover:bg-slate-50/80 dark:hover:bg-slate-800/40">
                        <td className="p-3 font-mono text-xs font-semibold">{b.doc_no}</td>
                        <td className="p-3">{fmtDate(b.date)}</td>
                        <td className="p-3 max-w-[180px] truncate">{b.party_name}</td>
                        <td className="p-3 font-mono text-xs text-slate-500">{b.supplier_invoice_no || "—"}</td>
                        <td className="p-3 text-right"><Money value={b.total} className="font-semibold" /></td>
                        <td className="p-3 text-right"><Money value={b.paid} className="text-emerald-600" /></td>
                        <td className="p-3 text-right"><Money value={b.balance} className={b.balance > 0 ? "text-red-600" : "text-slate-400"} /></td>
                        <td className="p-3"><StatusBadge status={b.status === "posted" ? (b.balance <= 0 ? "Paid" : b.paid > 0 ? "Partially Paid" : "Pending") : b.status === "draft" ? "Draft" : "Cancelled"} /></td>
                        <td className="p-3">
                          <div className="flex gap-1 justify-end">
                            {b.status === "draft" && <Button size="sm" variant="outline" data-testid={`bill-post-${b.doc_no}`} onClick={() => postBill(b)}><Check className="mr-1 h-3.5 w-3.5" /> Post</Button>}
                            {b.status === "posted" && b.paid <= 0 && <Button size="sm" variant="ghost" className="text-red-500" data-testid={`bill-cancel-${b.doc_no}`} onClick={() => cancelBill(b)}><Ban className="h-3.5 w-3.5" /></Button>}
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </TabsContent>

        <TabsContent value="orders">
          <div className="rounded-xl border border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-900 overflow-hidden">
            {!orders ? <TableSkeleton /> : orders.length === 0 ? (
              <EmptyState testId="orders-empty" title="No purchase orders" body="Create a PO, get it approved, then convert it to a bill." actionLabel="New PO" actionTo="/purchases/orders/new" />
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full text-sm" data-testid="orders-table">
                  <thead><tr className="bg-slate-50 dark:bg-slate-800/60 text-left text-xs font-semibold uppercase tracking-wider text-slate-500">
                    <th className="p-3">PO No</th><th className="p-3">Date</th><th className="p-3">Supplier</th>
                    <th className="p-3">Expected</th><th className="p-3 text-right">Total</th><th className="p-3">Status</th><th className="p-3"></th></tr></thead>
                  <tbody>
                    {orders.map((po) => (
                      <tr key={po.id} data-testid={`po-row-${po.doc_no}`} className="border-b border-slate-100 dark:border-slate-800 hover:bg-slate-50/80 dark:hover:bg-slate-800/40">
                        <td className="p-3 font-mono text-xs font-semibold">{po.doc_no}</td>
                        <td className="p-3">{fmtDate(po.date)}</td>
                        <td className="p-3 max-w-[180px] truncate">{po.party_name}</td>
                        <td className="p-3">{fmtDate(po.expected_delivery)}</td>
                        <td className="p-3 text-right"><Money value={po.total} className="font-semibold" /></td>
                        <td className="p-3"><StatusBadge status={PO_LABEL[po.status]} /></td>
                        <td className="p-3">
                          <div className="flex gap-1 justify-end flex-wrap">
                            {po.status === "draft" && <Button size="sm" variant="outline" data-testid={`po-submit-${po.doc_no}`} onClick={() => poAction(po, "pending_approval")}><Send className="mr-1 h-3.5 w-3.5" /> Submit</Button>}
                            {po.status === "pending_approval" && <Button size="sm" variant="outline" data-testid={`po-approve-${po.doc_no}`} onClick={() => poAction(po, "approved")}><CheckCheck className="mr-1 h-3.5 w-3.5" /> Approve</Button>}
                            {["approved", "pending_approval"].includes(po.status) && <Button size="sm" data-testid={`po-convert-${po.doc_no}`} onClick={() => poConvert(po)} className="bg-slate-900 dark:bg-slate-100 dark:text-slate-900"><ArrowRight className="mr-1 h-3.5 w-3.5" /> To Bill</Button>}
                            {!["converted", "cancelled"].includes(po.status) && <Button size="sm" variant="ghost" className="text-red-500" data-testid={`po-cancel-${po.doc_no}`} onClick={() => poAction(po, "cancelled")}><Ban className="h-3.5 w-3.5" /></Button>}
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </TabsContent>

        <TabsContent value="payables">
          <div className="rounded-xl border border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-900 overflow-hidden">
            {!payables ? <TableSkeleton /> : payables.items.length === 0 ? (
              <EmptyState testId="payables-empty" title="Nothing owed" body="All supplier bills are settled." />
            ) : (
              <>
                <div className="border-b border-slate-100 dark:border-slate-800 p-4 text-sm">
                  Total payables: <Money value={payables.total_balance} className="font-bold text-red-600" />
                </div>
                <table className="w-full text-sm" data-testid="payables-table">
                  <thead><tr className="bg-slate-50 dark:bg-slate-800/60 text-left text-xs font-semibold uppercase tracking-wider text-slate-500">
                    <th className="p-3">Bill No</th><th className="p-3">Supplier</th><th className="p-3">Date</th>
                    <th className="p-3 text-right">Total</th><th className="p-3 text-right">Paid</th><th className="p-3 text-right">Balance</th></tr></thead>
                  <tbody>
                    {payables.items.map((b) => (
                      <tr key={b.id} className="border-b border-slate-100 dark:border-slate-800">
                        <td className="p-3 font-mono text-xs font-semibold">{b.doc_no}</td>
                        <td className="p-3">{b.party_name}</td>
                        <td className="p-3">{fmtDate(b.date)}</td>
                        <td className="p-3 text-right"><Money value={b.total} /></td>
                        <td className="p-3 text-right"><Money value={b.paid} /></td>
                        <td className="p-3 text-right"><Money value={b.balance} className="font-semibold text-red-600" /></td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </>
            )}
          </div>
        </TabsContent>

        <TabsContent value="payouts">
          <div className="mb-3 flex justify-end">
            <Button data-testid="new-payout-btn" onClick={() => setPayDialog(true)}
              className="bg-slate-900 dark:bg-slate-100 dark:text-slate-900"><Plus className="mr-1.5 h-4 w-4" /> New Payment Out</Button>
          </div>
          <div className="rounded-xl border border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-900 overflow-hidden">
            {!payouts ? <TableSkeleton /> : payouts.length === 0 ? (
              <EmptyState testId="payouts-empty" title="No supplier payments" body="Record a payment out and allocate it to supplier bills." />
            ) : (
              <table className="w-full text-sm" data-testid="payouts-table">
                <thead><tr className="bg-slate-50 dark:bg-slate-800/60 text-left text-xs font-semibold uppercase tracking-wider text-slate-500">
                  <th className="p-3">Payment No</th><th className="p-3">Date</th><th className="p-3">Supplier</th>
                  <th className="p-3">Mode</th><th className="p-3 text-right">Amount</th><th className="p-3 text-right">Allocated</th>
                  <th className="p-3 text-right">Prepaid</th><th className="p-3"></th></tr></thead>
                <tbody>
                  {payouts.map((p) => (
                    <tr key={p.id} data-testid={`payout-row-${p.payment_no}`} className="border-b border-slate-100 dark:border-slate-800">
                      <td className="p-3 font-mono text-xs font-semibold">{p.payment_no}</td>
                      <td className="p-3">{fmtDate(p.date)}</td>
                      <td className="p-3 max-w-[180px] truncate">{p.party_name}</td>
                      <td className="p-3 capitalize text-slate-500">{p.mode?.replace(/_/g, " ")}</td>
                      <td className="p-3 text-right"><Money value={p.amount} className="font-semibold" /></td>
                      <td className="p-3 text-right"><Money value={p.allocated} className="text-emerald-600" /></td>
                      <td className="p-3 text-right"><Money value={p.unallocated} className={p.unallocated > 0 ? "text-amber-600 font-semibold" : "text-slate-400"} /></td>
                      <td className="p-3"><Button size="sm" variant="ghost" className="text-red-500 h-7" data-testid={`payout-cancel-${p.payment_no}`} onClick={() => cancelPayout(p)}><Ban className="h-3.5 w-3.5" /></Button></td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
        </TabsContent>
      </Tabs>

      <PayOutDialog open={payDialog} onClose={() => setPayDialog(false)} onSaved={load} />
    </div>
  );
}
